/**
 * Centralized React Query Keys
 *
 * This file defines all query keys used in the application to:
 * 1. Prevent cache staleness by ensuring consistent key usage
 * 2. Enable proper invalidation of related queries
 * 3. Make query key dependencies explicit and maintainable
 *
 * Usage:
 *   import { queryKeys } from '@/src/services/queryKeys';
 *   useQuery({ queryKey: queryKeys.documents.list(filters), ... })
 *   queryClient.invalidateQueries({ queryKey: queryKeys.documents.all })
 */

import { DocumentUploadFilters } from '../types';

// Base key prefixes for grouping related queries
const KEYS = {
  auth: 'auth',
  companies: 'companies',
  company: 'company',
  complexes: 'complexes',
  documents: 'documents',
  admin: 'admin',
  uploads: 'uploads',
  storage: 'storage',
  users: 'users',
  customers: 'customers',
  database: 'database',
  webhook: 'webhook',
  analysis: 'analysis',
  emails: 'emails',
} as const;

export const queryKeys = {
  // ============================================
  // AUTH QUERIES (consolidated from authService.ts)
  // ============================================
  auth: {
    /** Invalidates ALL auth-related queries */
    all: [KEYS.auth] as const,
    /** Current user query */
    currentUser: [KEYS.auth, 'currentUser'] as const,
    /** Session info query */
    sessionInfo: [KEYS.auth, 'sessionInfo'] as const,
    /** Auth statistics */
    statistics: [KEYS.auth, 'statistics'] as const,
  },

  // ============================================
  // COMPANY QUERIES (Item 9 - companies table optimization)
  // ============================================
  companies: {
    /** Invalidates ALL company-related queries */
    all: [KEYS.companies] as const,
    /** List of all companies (super admin) */
    list: () => [KEYS.companies, 'list'] as const,
    /** Stats for a specific company (null = all companies for SUPER_ADMIN) */
    stats: (companyId: string | null) => [KEYS.company, 'stats', companyId ?? 'all'] as const,
    /** Verified ESA letters stats for charting (null = all companies for SUPER_ADMIN) */
    verifiedLetters: (companyId: string | null, days: number | 'all') =>
      [KEYS.company, 'verifiedLetters', companyId ?? 'all', days] as const,
    /** Platform-wide company statistics (SUPER_ADMIN) */
    statistics: () => [KEYS.companies, 'statistics'] as const,
    /** Detailed statistics for a specific company */
    detailStatistics: (companyId: string) =>
      [KEYS.companies, 'detailStatistics', companyId] as const,
    /** Search companies by name (fuzzy) */
    search: (term: string) => [KEYS.companies, 'search', term] as const,
    /** Paginated companies list (SUPER_ADMIN) */
    paginated: (page: number, pageSize: number, sortBy?: string, sortOrder?: string) =>
      [KEYS.companies, 'paginated', page, pageSize, sortBy, sortOrder] as const,
    /** Single company detail */
    detail: (companyId: string) => [KEYS.companies, 'detail', companyId] as const,
  },

  // ============================================
  // COMPLEX QUERIES
  // ============================================
  complexes: {
    /** Invalidates ALL complex-related queries */
    all: [KEYS.complexes] as const,
    /** Paginated complexes for a company (null = all companies for SUPER_ADMIN) */
    paginated: (
      companyId: string | null,
      limit: number,
      assignedComplexIds?: string
    ) => [KEYS.complexes, 'paginated', companyId ?? 'all', limit, assignedComplexIds] as const,
    /** All complexes for a company (non-paginated) */
    list: (companyId: string) => [KEYS.complexes, 'list', companyId] as const,
    /** Customers for a specific complex */
    customers: (complexId: string) => [KEYS.complexes, 'customers', complexId] as const,
    /** Single complex by ID */
    detail: (complexId: string) => [KEYS.complexes, 'detail', complexId] as const,
    /** Search complexes by name/address */
    search: (companyId: string, term: string) => [KEYS.complexes, 'search', companyId, term] as const,
    /** Complex statistics for a company */
    statistics: (companyId: string) => [KEYS.complexes, 'statistics', companyId] as const,
    /** Complex summaries (lightweight list) */
    summaries: (companyId: string) => [KEYS.complexes, 'summaries', companyId] as const,
    /** Uploads for a specific complex */
    uploads: (complexId: string, limit: number, offset: number) =>
      [KEYS.complexes, 'uploads', complexId, limit, offset] as const,
    /** Complex count for a company */
    count: (companyId: string) => [KEYS.complexes, 'count', companyId] as const,
    // ============================================
    // Item 11: RPC-based queries for complexes table
    // ============================================
    /** Platform-wide complex statistics (SUPER_ADMIN only) - uses RPC */
    platformStatistics: () => [KEYS.complexes, 'platformStatistics'] as const,
    /** Company-specific complex statistics - uses RPC */
    companyStatistics: (companyId: string) =>
      [KEYS.complexes, 'companyStatistics', companyId] as const,
    /** Fuzzy search complexes using RPC with trigram similarity */
    rpcSearch: (companyId: string | null, term: string, limit: number) =>
      [KEYS.complexes, 'rpcSearch', companyId, term, limit] as const,
    /** RPC-based paginated complexes with advanced filters */
    rpcPaginated: (
      companyId: string | null,
      page: number,
      pageSize: number,
      sortBy: string,
      sortOrder: string,
      hasUploads: boolean | null
    ) => [KEYS.complexes, 'rpcPaginated', companyId, page, pageSize, sortBy, sortOrder, hasUploads] as const,
    /** Complex upload summary (dashboard cards) - uses RPC */
    uploadSummary: (complexId: string) =>
      [KEYS.complexes, 'uploadSummary', complexId] as const,
  },

  // ============================================
  // DOCUMENT QUERIES
  // ============================================
  documents: {
    /** Invalidates ALL document-related queries */
    all: [KEYS.documents] as const,
    /** Document uploads with optional filters */
    list: (filters?: DocumentUploadFilters) =>
      [KEYS.documents, 'list', filters] as const,
    /** Documents for a specific complex */
    byComplex: (complexId: string) =>
      [KEYS.documents, 'byComplex', complexId] as const,
    /** Single document by ID */
    detail: (uploadId: string) => [KEYS.documents, 'detail', uploadId] as const,
    /** Documents by user email */
    byEmail: (email: string) => [KEYS.documents, 'byEmail', email] as const,
    /** All uploads (paginated) */
    allUploads: (limit: number, offset: number) =>
      [KEYS.documents, 'allUploads', limit, offset] as const,
    /** Aggregate upload statistics */
    statistics: () => [KEYS.documents, 'statistics'] as const,
    /** Count of pending documents */
    pendingCount: () => [KEYS.documents, 'pendingCount'] as const,
    /** Count of completed documents */
    completedCount: () => [KEYS.documents, 'completedCount'] as const,
    /** Paginated uploads with total count (optimized) */
    paginated: (limit: number, offset: number, filters?: DocumentUploadFilters) =>
      [KEYS.documents, 'paginated', limit, offset, filters] as const,
    /** All status counts combined (optimized) */
    statusCounts: () => [KEYS.documents, 'statusCounts'] as const,
    /** List view with reduced columns (optimized) */
    listView: (filters?: DocumentUploadFilters) =>
      [KEYS.documents, 'listView', filters] as const,
    /** Search documents by name/email */
    search: (term: string) => [KEYS.documents, 'search', term] as const,
    /** Review queue */
    reviewQueue: (limit: number) => [KEYS.documents, 'reviewQueue', limit] as const,
    /** Recent uploads filtered by user's assigned complexes (Item 30) */
    recentForUser: (limit: number, assignedComplexIds?: string) =>
      [KEYS.documents, 'recentForUser', limit, assignedComplexIds] as const,
    /** Dashboard uploads with llm_result for verification display (Item 30) */
    dashboard: (limit: number, assignedComplexIds?: string) =>
      [KEYS.documents, 'dashboard', limit, assignedComplexIds] as const,
    /** Verification outcome counts for dashboard metrics (Item 30) */
    verificationCounts: (assignedComplexIds?: string) =>
      [KEYS.documents, 'verificationCounts', assignedComplexIds] as const,
    /** Access-controlled uploads with role-based filtering (Phase 2) */
    withAccessControl: (limit: number, offset: number) =>
      [KEYS.documents, 'withAccessControl', limit, offset] as const,
    /** Access level for a single upload */
    accessLevel: (uploadId: string) =>
      [KEYS.documents, 'accessLevel', uploadId] as const,
  },

  // ============================================
  // ADMIN QUERIES
  // ============================================
  admin: {
    /** Invalidates ALL admin-related queries */
    all: [KEYS.admin] as const,
    /** Platform-wide admin statistics */
    stats: () => [KEYS.admin, 'stats'] as const,
    /** RLS health check */
    rlsHealth: () => [KEYS.admin, 'rlsHealth'] as const,
  },

  // ============================================
  // UPLOAD QUERIES (optimized uploadService)
  // ============================================
  uploads: {
    /** Invalidates ALL upload-related queries */
    all: [KEYS.uploads] as const,
    /** Upload statistics for monitoring */
    statistics: () => [KEYS.uploads, 'statistics'] as const,
    /** Recent uploads with pagination */
    recent: (limit: number, offset: number) =>
      [KEYS.uploads, 'recent', limit, offset] as const,
    /** Uploads by processing status */
    byStatus: (status: 'pending' | 'processing' | 'completed' | 'failed', limit: number) =>
      [KEYS.uploads, 'byStatus', status, limit] as const,
  },

  // ============================================
  // STORAGE QUERIES (optimized storageService + Item 15 admin functions)
  // ============================================
  storage: {
    /** Invalidates ALL storage-related queries */
    all: [KEYS.storage] as const,
    /** Circuit breaker states for monitoring */
    circuitBreakers: () => [KEYS.storage, 'circuitBreakers'] as const,
    /** List files in a bucket */
    files: (bucket: string, prefix?: string, limit?: number, offset?: number) =>
      [KEYS.storage, 'files', bucket, prefix, limit, offset] as const,
    /** Check file existence */
    fileExists: (path: string, bucket?: string) =>
      [KEYS.storage, 'exists', path, bucket] as const,
    /** Get file size */
    fileSize: (path: string, bucket?: string) =>
      [KEYS.storage, 'size', path, bucket] as const,
    /** Signed URL (with path as key for caching) */
    signedUrl: (path: string, bucket?: string) =>
      [KEYS.storage, 'signedUrl', path, bucket] as const,
    /** Batch signed URLs */
    batchSignedUrls: (paths: string[], bucket?: string) =>
      [KEYS.storage, 'batchSignedUrls', paths, bucket] as const,
    // ============================================
    // Item 15: Storage Admin Queries (SUPER_ADMIN only)
    // ============================================
    /** Bucket statistics (file count, size, etc.) */
    bucketStats: (bucketId: string) =>
      [KEYS.storage, 'bucketStats', bucketId] as const,
    /** Orphaned files (files without database records) */
    orphanedFiles: (bucketId?: string, limit?: number) =>
      [KEYS.storage, 'orphanedFiles', bucketId, limit] as const,
    /** Storage access log */
    accessLog: (bucketId: string, days: number, limit: number) =>
      [KEYS.storage, 'accessLog', bucketId, days, limit] as const,
    /** Storage usage breakdown by company */
    usageByCompany: () =>
      [KEYS.storage, 'usageByCompany'] as const,
    /** File integrity check */
    integrityCheck: (bucketId: string) =>
      [KEYS.storage, 'integrityCheck', bucketId] as const,
    /** Comprehensive health report */
    healthReport: () =>
      [KEYS.storage, 'healthReport'] as const,
    // ============================================
    // Item 16: anonymous-uploads bucket specific keys
    // ============================================
    /** Anonymous-uploads bucket statistics (SUPER_ADMIN/COMPANY_OWNER) */
    anonymousUploadsBucketStats: () =>
      [KEYS.storage, 'anonymousUploadsBucketStats'] as const,
    /** Orphaned files in anonymous-uploads (SUPER_ADMIN) */
    anonymousUploadsOrphanedFiles: (limit?: number) =>
      [KEYS.storage, 'anonymousUploadsOrphanedFiles', limit] as const,
    /** Access log for anonymous-uploads (SUPER_ADMIN/COMPANY_OWNER) */
    anonymousUploadsAccessLog: (days: number, limit: number) =>
      [KEYS.storage, 'anonymousUploadsAccessLog', days, limit] as const,
    /** Company usage for anonymous-uploads (SUPER_ADMIN) */
    anonymousUploadsUsageByCompany: () =>
      [KEYS.storage, 'anonymousUploadsUsageByCompany'] as const,
    /** File integrity check for anonymous-uploads (SUPER_ADMIN) */
    anonymousUploadsIntegrityCheck: () =>
      [KEYS.storage, 'anonymousUploadsIntegrityCheck'] as const,
    /** Health report for anonymous-uploads (SUPER_ADMIN) */
    anonymousUploadsHealthReport: () =>
      [KEYS.storage, 'anonymousUploadsHealthReport'] as const,
    // ============================================
    // Item 26: Storage → Database Integration keys
    // ============================================
    /** Storage → Database sync health metrics (SUPER_ADMIN) */
    storageDatabaseSyncHealth: () =>
      [KEYS.storage, 'storageDatabaseSyncHealth'] as const,
    /** Incomplete uploads stuck in a status (SUPER_ADMIN) */
    incompleteUploads: (status?: string, olderThanMinutes?: number, limit?: number) =>
      [KEYS.storage, 'incompleteUploads', status, olderThanMinutes, limit] as const,
    /** Verify upload storage exists (SUPER_ADMIN) */
    verifyUploadStorage: (uploadId: string) =>
      [KEYS.storage, 'verifyUploadStorage', uploadId] as const,
    /** Uploads pending manual review (SUPER_ADMIN) */
    uploadsPendingManualReview: (limit?: number) =>
      [KEYS.storage, 'uploadsPendingManualReview', limit] as const,
  },

  // ============================================
  // USER QUERIES (optimized dataService - Item 17 + Item 10 RPC functions)
  // ============================================
  users: {
    /** Invalidates ALL user-related queries */
    all: [KEYS.users] as const,
    /** Users list for a company */
    list: (companyId: string) => [KEYS.users, 'list', companyId] as const,
    /** Paginated users for a company */
    paginated: (companyId: string, limit: number, offset: number) =>
      [KEYS.users, 'paginated', companyId, limit, offset] as const,
    /** Single user by ID */
    detail: (userId: string) => [KEYS.users, 'detail', userId] as const,
    /** User by email */
    byEmail: (email: string) => [KEYS.users, 'byEmail', email] as const,
    /** Search users by name/email */
    search: (companyId: string, term: string) =>
      [KEYS.users, 'search', companyId, term] as const,
    /** Users by role */
    byRole: (companyId: string, role: string) =>
      [KEYS.users, 'byRole', companyId, role] as const,
    /** User statistics for a company */
    statistics: (companyId: string) =>
      [KEYS.users, 'statistics', companyId] as const,
    // ============================================
    // Item 10: RPC-based queries for users table
    // ============================================
    /** Platform-wide user statistics (SUPER_ADMIN only) - uses RPC */
    platformStatistics: () => [KEYS.users, 'platformStatistics'] as const,
    /** Fuzzy search users using RPC with trigram similarity */
    rpcSearch: (companyId: string | null, term: string, limit: number) =>
      [KEYS.users, 'rpcSearch', companyId, term, limit] as const,
    /** RPC-based paginated users with advanced filters */
    rpcPaginated: (
      companyId: string | null,
      page: number,
      pageSize: number,
      role: string | null,
      isActive: boolean | null,
      sortBy: string,
      sortOrder: string
    ) => [KEYS.users, 'rpcPaginated', companyId, page, pageSize, role, isActive, sortBy, sortOrder] as const,
    /** User activity summary for monitoring login patterns */
    activitySummary: (companyId: string | null, days: number) =>
      [KEYS.users, 'activitySummary', companyId, days] as const,
  },

  // ============================================
  // CUSTOMER QUERIES (Item 12 - customers table optimization)
  // ============================================
  customers: {
    /** Invalidates ALL customer-related queries */
    all: [KEYS.customers] as const,
    /** Customers list for a complex */
    list: (complexId: string) => [KEYS.customers, 'list', complexId] as const,
    /** Single customer by ID */
    detail: (customerId: string) => [KEYS.customers, 'detail', customerId] as const,
    /** Customer statistics for a complex - uses RPC */
    complexStatistics: (complexId: string) =>
      [KEYS.customers, 'complexStatistics', complexId] as const,
    /** Customer statistics for a company - uses RPC */
    companyStatistics: (companyId: string) =>
      [KEYS.customers, 'companyStatistics', companyId] as const,
    /** Search customers using RPC with trigram similarity */
    rpcSearch: (searchTerm: string, complexId?: string, companyId?: string, limit?: number) =>
      [KEYS.customers, 'rpcSearch', searchTerm, complexId, companyId, limit] as const,
    /** RPC-based paginated customers with optional filters */
    rpcPaginated: (
      page: number,
      pageSize: number,
      complexId?: string,
      companyId?: string,
      status?: string,
      sortBy?: string,
      sortOrder?: string
    ) => [KEYS.customers, 'rpcPaginated', page, pageSize, complexId, companyId, status, sortBy, sortOrder] as const,
    /** Pending customers for review queue - uses RPC */
    pendingReview: (complexId?: string, companyId?: string, limit?: number) =>
      [KEYS.customers, 'pendingReview', complexId, companyId, limit] as const,
    /** Customers by status */
    byStatus: (status: 'pending' | 'approved' | 'rejected', complexId?: string) =>
      [KEYS.customers, 'byStatus', status, complexId] as const,
  },

  // ============================================
  // DATABASE ADMIN QUERIES (Item 14)
  // ============================================
  database: {
    /** Invalidates ALL database admin queries */
    all: ['database'] as const,
    /** Index usage statistics */
    indexUsage: () => ['database', 'indexUsage'] as const,
    /** Unused indexes report */
    unusedIndexes: (minScans?: number) => ['database', 'unusedIndexes', minScans] as const,
    /** Missing foreign key indexes */
    missingFkIndexes: () => ['database', 'missingFkIndexes'] as const,
    /** Table statistics (size, row count) */
    tableStatistics: () => ['database', 'tableStatistics'] as const,
    /** Index health report */
    indexHealth: () => ['database', 'indexHealth'] as const,
  },

  // ============================================
  // WEBHOOK MONITORING QUERIES (Item 27 - Stripe → Webhook integration)
  // ============================================
  webhook: {
    /** Invalidates ALL webhook monitoring queries */
    all: [KEYS.webhook] as const,
    /** Webhook health statistics over time period */
    healthStats: (hours: number) => [KEYS.webhook, 'healthStats', hours] as const,
    /** Recent webhook events list */
    recentEvents: (params: { limit?: number; eventType?: string; status?: string }) =>
      [KEYS.webhook, 'recentEvents', params] as const,
    /** Signature validation failure logs */
    signatureFailures: (hours: number, limit: number) =>
      [KEYS.webhook, 'signatureFailures', hours, limit] as const,
    /** Latency trends (hourly buckets) */
    latencyTrends: (hours: number) => [KEYS.webhook, 'latencyTrends', hours] as const,
    /** Configuration health checks */
    configHealth: () => [KEYS.webhook, 'configHealth'] as const,
  },

  // ============================================
  // EMAIL TRACKING QUERIES (Cycle 33 - emailTrackingService optimization)
  // ============================================
  emails: {
    /** Invalidates ALL email-related queries */
    all: [KEYS.emails] as const,
    /** Email statistics for a specific user */
    userStats: (userId: string) => [KEYS.emails, 'userStats', userId] as const,
    /** Email analytics for a date range */
    analytics: (startDate: string, endDate: string) =>
      [KEYS.emails, 'analytics', startDate, endDate] as const,
    /** Recent email events */
    recentEvents: (limit: number) => [KEYS.emails, 'recentEvents', limit] as const,
    /** Email statistics summary (last 30 days) */
    summary: () => [KEYS.emails, 'summary'] as const,
    /** Emails by type */
    byType: (emailType: string, limit: number) =>
      [KEYS.emails, 'byType', emailType, limit] as const,
    /** Search emails by recipient */
    search: (searchTerm: string, limit: number) =>
      [KEYS.emails, 'search', searchTerm, limit] as const,
  },

  // ============================================
  // ANALYSIS QUERIES (Cycle 32 - analysisService optimization)
  // ============================================
  analysis: {
    /** Invalidates ALL analysis queries */
    all: [KEYS.analysis] as const,
    /** Analysis result for a specific document */
    result: (documentId: string) => [KEYS.analysis, 'result', documentId] as const,
    /** Analysis result by correlation ID (for tracking in-progress analysis) */
    byCorrelationId: (correlationId: string) =>
      [KEYS.analysis, 'byCorrelationId', correlationId] as const,
    /** Analysis cache (recently analyzed documents) */
    cache: () => [KEYS.analysis, 'cache'] as const,
    /** Analysis statistics (total analyzed, average score, etc.) */
    statistics: () => [KEYS.analysis, 'statistics'] as const,
    /** Analysis results by score range */
    byScoreRange: (minScore: number, maxScore: number) =>
      [KEYS.analysis, 'byScoreRange', minScore, maxScore] as const,
    /** Analysis results by status (verified, flagged, pending review) */
    byStatus: (status: 'verified' | 'flagged' | 'pending_review') =>
      [KEYS.analysis, 'byStatus', status] as const,
    /** Recent analysis results */
    recent: (limit: number) => [KEYS.analysis, 'recent', limit] as const,
  },

  // ============================================
  // ANALYTICS DASHBOARD QUERIES (Analytics Dashboard feature)
  // ============================================
  analytics: {
    /** Invalidates ALL analytics dashboard queries */
    all: ['analytics'] as const,
    /** Analytics summary with metrics and trends */
    summary: (period: string, companyId?: string) =>
      ['analytics', 'summary', period, companyId] as const,
    /** Upload trends over time */
    uploadTrends: (period: string, companyId?: string) =>
      ['analytics', 'uploadTrends', period, companyId] as const,
    /** Revenue trends over time */
    revenueTrends: (period: string, companyId?: string) =>
      ['analytics', 'revenueTrends', period, companyId] as const,
    /** Revenue breakdown by property/complex */
    revenueByProperty: (period: string, companyId?: string, limit?: number) =>
      ['analytics', 'revenueByProperty', period, companyId, limit] as const,
    /** Custom date range analytics */
    dateRange: (startDate: string, endDate: string, companyId?: string) =>
      ['analytics', 'dateRange', startDate, endDate, companyId] as const,
  },
} as const;

/**
 * Invalidation helpers for mutations
 *
 * These define which queries should be invalidated together when data changes.
 * Use these in mutation onSuccess callbacks to ensure all related data refreshes.
 */
export const invalidationGroups = {
  /**
   * Invalidate after document property/status changes
   * Refreshes: document detail, document lists, statistics, counts
   */
  documentUpdate: (uploadId: string) => [
    { queryKey: queryKeys.documents.detail(uploadId) },
    { queryKey: queryKeys.documents.all },
  ],

  /**
   * Invalidate after payment status changes
   * Refreshes: document detail, document lists, statistics
   */
  paymentUpdate: (uploadId: string) => [
    { queryKey: queryKeys.documents.detail(uploadId) },
    { queryKey: queryKeys.documents.all },
  ],

  /**
   * Invalidate after processing status changes
   * Refreshes: document detail, document lists, statistics, counts, verified letters
   */
  processingUpdate: (uploadId: string, companyId?: string) => [
    { queryKey: queryKeys.documents.detail(uploadId) },
    { queryKey: queryKeys.documents.all },
    ...(companyId ? [{ queryKey: queryKeys.companies.all }] : []),
  ],

  /**
   * Invalidate after company creation (Item 9 - enhanced)
   * Refreshes: company list, statistics, paginated
   */
  companyCreate: () => [
    { queryKey: queryKeys.companies.all },
    { queryKey: queryKeys.companies.statistics() },
    { queryKey: queryKeys.admin.stats() },
  ],

  /**
   * Invalidate after company update (Item 9)
   * Refreshes: company detail, list, stats
   */
  companyUpdate: (companyId: string) => [
    { queryKey: queryKeys.companies.detail(companyId) },
    { queryKey: queryKeys.companies.all },
    { queryKey: queryKeys.companies.stats(companyId) },
    { queryKey: queryKeys.companies.detailStatistics(companyId) },
  ],

  /**
   * Invalidate after company deletion (Item 9)
   * Refreshes: company list, statistics, admin stats
   */
  companyDelete: () => [
    { queryKey: queryKeys.companies.all },
    { queryKey: queryKeys.companies.statistics() },
    { queryKey: queryKeys.admin.stats() },
    { queryKey: queryKeys.complexes.all },
    { queryKey: queryKeys.users.all },
  ],

  /**
   * Invalidate after upload operations
   * Refreshes: upload statistics, recent uploads, documents
   */
  uploadCreate: () => [
    { queryKey: queryKeys.uploads.all },
    { queryKey: queryKeys.documents.all },
  ],

  /**
   * Invalidate after upload status changes
   * Refreshes: upload statistics, uploads by status
   */
  uploadStatusUpdate: () => [
    { queryKey: queryKeys.uploads.all },
  ],

  /**
   * Invalidate after user creation (Item 17)
   * Refreshes: user lists, user statistics, admin stats
   */
  userCreate: (companyId: string) => [
    { queryKey: queryKeys.users.all },
    { queryKey: queryKeys.users.list(companyId) },
    { queryKey: queryKeys.users.statistics(companyId) },
    { queryKey: queryKeys.admin.stats() },
  ],

  /**
   * Invalidate after user deletion (Item 17)
   * Refreshes: user lists, statistics, admin stats
   */
  userDelete: (companyId: string) => [
    { queryKey: queryKeys.users.all },
    { queryKey: queryKeys.users.list(companyId) },
    { queryKey: queryKeys.users.statistics(companyId) },
    { queryKey: queryKeys.admin.stats() },
  ],

  /**
   * Invalidate after user role/assignment changes (Item 17)
   * Refreshes: user detail, user lists by role, complexes
   */
  userRoleChange: (userId: string, companyId: string) => [
    { queryKey: queryKeys.users.detail(userId) },
    { queryKey: queryKeys.users.all },
    { queryKey: queryKeys.users.list(companyId) },
    { queryKey: queryKeys.complexes.all },
  ],

  /**
   * Invalidate after document analysis completion
   * Refreshes: analysis result, statistics, document detail
   */
  analysisComplete: (documentId: string) => [
    { queryKey: queryKeys.analysis.result(documentId) },
    { queryKey: queryKeys.analysis.statistics() },
    { queryKey: queryKeys.analysis.cache() },
    { queryKey: queryKeys.documents.detail(documentId) },
    { queryKey: queryKeys.documents.all },
  ],

} as const;

// Type exports for external use
export type QueryKeys = typeof queryKeys;
export type InvalidationGroups = typeof invalidationGroups;
